import React from 'react';
import PropTypes from 'prop-types';
import { useMutation } from 'react-query';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { startOfYear } from 'date-fns';
import { sortBy } from 'lodash';
import Button from '../../../atoms/Button/Button';
import useSeasonService from '../../../../services/seasonService';
import seasonSchema from '../../../../validations/seasons/seasonSchema';
import SelectField from '../../../atoms/SelectField/SelectField';
import DatePickerInput from '../../../atoms/DatePickerInput/DatePickerInput';
import useModal from '../../../../utils/hooks/useModal';
import dayId from '../../../../utils/tools/dayId';
import useNotification from '../../../../utils/hooks/useNotification';

function SeasonCreationForm({ seasonOptions, setSeasons }) {
  const { t } = useTranslation();
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const { postSeason } = useSeasonService();
  const { closeModal } = useModal();
  const { displayNotification } = useNotification();

  const { isPostLoading, mutate: postSeasonMutate } = useMutation({
    mutationFn: postSeason,
    onError: (error) => {
      displayNotification({
        message: t(error?.response?.data?.code
          ? `code_errors.${error.response.data.code}`
          : 'common.createError'),
        color: 'error',
      });
    },
    onSuccess: (res) => {
      if (res.data?.id) {
        setSeasons((prev) => sortBy([...prev, res.data], 'startDate'));
        displayNotification({ message: t('seasons.table.add.success') });
      }
    },
  });

  const formik = useFormik({
    initialValues: {
      type: null,
      startDate: null,
    },
    onSubmit: (values) => {
      postSeasonMutate({
        type: values.type?.value,
        startDate: dayId.getDayIdFromDate(values.startDate),
      });
    },
    validationSchema: seasonSchema,
  });

  return (
    <div>
      <div className="m-b-20">
        <DatePickerInput
          id="newSeasonDate"
          label={t('seasons.table.columns.period')}
          date={formik.values.startDate}
          setDate={(date) => formik.setFieldValue('startDate', date)}
          minDate={startOfYear(new Date())}
        />
      </div>
      <SelectField
        id="newSeasonType"
        name="type"
        options={seasonOptions}
        label={t('seasons.table.columns.season')}
        value={formik.values.type}
        setFieldValue={formik.setFieldValue}
        setFieldTouched={formik.setFieldTouched}
        clearable={false}
      />

      <div className="d-flex a-center j-end mobile-d-col a-stretch m-t-40">
        <Button
          theme="inverted"
          className="m-r-10 mobile-m-r-0 mobile-m-b-10"
          onClick={() => closeModal()}
          full={isMobile}
          disabled={isPostLoading}
        >
          {t('common.cancel')}
        </Button>
        <Button
          onClick={() => {
            formik.submitForm();
            closeModal();
          }}
          full={isMobile}
          isLoading={isPostLoading}
          disabled={!formik.dirty || !formik.isValid}
        >
          {t('common.add')}
        </Button>
      </div>
    </div>
  );
}

SeasonCreationForm.propTypes = {
  seasonOptions: PropTypes.arrayOf(PropTypes.shape({})),
  setSeasons: PropTypes.func,
};

SeasonCreationForm.defaultProps = {
  seasonOptions: [],
  setSeasons: () => {},
};

export default SeasonCreationForm;
